import { useMemo, useState } from 'react';
import { useCluvioDashboards, useCreateTemplate } from '@/api/hooks';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { CluvioOptionInput, CluvioMultiOptionInput } from '@/components/CluvioOptionInput';
import { PageHeader, Skeleton, EmptyState, Field } from '@/components/primitives';
import type { ComboboxOption } from '@/components/Combobox';
import {
  AGGREGATIONS,
  TENANT_TYPES,
  TIMERANGE_PRESETS,
  TIMERANGE_GROUP_ORDER,
  TIMERANGE_OPTIONS,
  AGGREGATION_OPTIONS,
  TIMERANGE_CONTROL_TYPES,
  AGGREGATION_CONTROL_TYPES,
  isMultiControl,
} from '@/lib/cluvio';
import { toast } from 'react-hot-toast';
import { Loader2, Wand2, Search, Check, BarChart3 } from 'lucide-react';
import { cn } from '@/lib/utils';

function StepCard({
  step,
  title,
  description,
  done,
  children,
}: {
  step: number;
  title: string;
  description?: string;
  done?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Card className="p-5 animate-enter">
      <div className="flex items-center gap-3 mb-4">
        <span
          className={cn(
            'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold transition-colors',
            done ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
          )}
        >
          {done ? <Check className="h-3.5 w-3.5" /> : step}
        </span>
        <div>
          <h2 className="text-sm font-semibold leading-tight">{title}</h2>
          {description && <p className="text-xs text-muted-foreground mt-0.5">{description}</p>}
        </div>
      </div>
      {children}
    </Card>
  );
}

function OptionChips({
  options,
  selected,
  onToggle,
}: {
  options: { label: string; value: string }[];
  selected: string[];
  onToggle: (value: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => {
        const isOn = selected.includes(o.value);
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onToggle(o.value)}
            className={cn(
              'inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium transition-all active:scale-95 select-none',
              isOn
                ? 'border-primary bg-primary text-primary-foreground shadow-card'
                : 'border-border bg-background text-muted-foreground hover:border-primary/40 hover:text-foreground'
            )}
          >
            {isOn && <Check className="h-3 w-3 mr-1" />}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Default-value field for a variable filter. Date-range and aggregation
 * filters suggest the Cluvio vocabulary (the options endpoint returns nothing
 * for them); everything else suggests the filter's actual Cluvio options.
 */
function FilterDefaultField({
  filter,
  value,
  onChange,
}: {
  filter: { id: string; variable_name: string; controlType?: string };
  value?: ComboboxOption | ComboboxOption[];
  onChange: (opt: ComboboxOption | ComboboxOption[]) => void;
}) {
  const isRange =
    TIMERANGE_CONTROL_TYPES.has(filter.controlType ?? '') || filter.variable_name === 'timerange';
  const isAgg =
    !isRange &&
    (AGGREGATION_CONTROL_TYPES.has(filter.controlType ?? '') || filter.variable_name === 'aggregation');
  const presets = isRange ? TIMERANGE_OPTIONS : isAgg ? AGGREGATION_OPTIONS : undefined;
  const multi = !presets && isMultiControl(filter.controlType);

  if (multi) {
    return (
      <div className="space-y-1">
        <span className="text-[11px] font-medium text-muted-foreground">
          Default values (optional)
          <span className="text-primary"> · multi-select</span>
        </span>
        <CluvioMultiOptionInput
          filterId={filter.id}
          values={Array.isArray(value) ? value : []}
          onChange={onChange}
        />
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <span className="text-[11px] font-medium text-muted-foreground">
        Default value (optional)
        {isRange && <span className="text-primary"> · time range preset</span>}
        {isAgg && <span className="text-primary"> · aggregation</span>}
      </span>
      <CluvioOptionInput
        filterId={filter.id}
        presets={presets}
        value={Array.isArray(value) ? undefined : value}
        onChange={onChange}
        placeholder={presets ? 'Select a preset…' : 'Type to search Cluvio options…'}
      />
      {isRange && (
        <p className="text-[10px] leading-snug text-muted-foreground/80">
          Cluvio time range vocabulary — pick a preset or type a custom token (e.g. 7_full_day).
        </p>
      )}
    </div>
  );
}

export function TemplateGeneratorPage() {
  const { data: dashboards, isLoading } = useCluvioDashboards();
  const createMutation = useCreateTemplate();

  const [selectedDashboard, setSelectedDashboard] = useState<string | null>(null);
  const [typeOfTenant, setTypeOfTenant] = useState<string>('brand');
  const [fixedFilterIds, setFixedFilterIds] = useState<Set<string>>(new Set());
  const [variableFilterIds, setVariableFilterIds] = useState<Set<string>>(new Set());
  const [limitedFilterIds, setLimitedFilterIds] = useState<Set<string>>(new Set());
  const [defaults, setDefaults] = useState<Record<string, ComboboxOption | ComboboxOption[]>>({});
  const [placeholders, setPlaceholders] = useState<Record<string, string>>({});
  const [enableCollapsible, setEnableCollapsible] = useState(false);
  const [filtersVisible, setFiltersVisible] = useState(true);
  const [hasAggregation, setHasAggregation] = useState(false);
  const [selectedAggs, setSelectedAggs] = useState<string[]>([]);
  const [defaultAgg, setDefaultAgg] = useState('');
  const [hasTimerange, setHasTimerange] = useState(false);
  const [selectedRanges, setSelectedRanges] = useState<string[]>([]);
  const [defaultRange, setDefaultRange] = useState('');
  const [allowCustom, setAllowCustom] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [description, setDescription] = useState('');
  const [defaultDashboardName, setDefaultDashboardName] = useState('');
  const [defaultSortOrder, setDefaultSortOrder] = useState('0');
  const [defaultDashboardDescription, setDefaultDashboardDescription] = useState('');
  const [dashboardSearch, setDashboardSearch] = useState('');
  const [fixedFilterSearch, setFixedFilterSearch] = useState('');
  const [varFilterSearch, setVarFilterSearch] = useState('');

  const dashboard = dashboards?.find((d) => d.id === selectedDashboard);
  const availableFilters = dashboard?.filters ?? [];
  const remainingFilters = availableFilters.filter((f) => !fixedFilterIds.has(f.id));

  const rangeByToken = useMemo(() => {
    const map = new Map<string, (typeof TIMERANGE_PRESETS)[number]>();
    for (const p of TIMERANGE_PRESETS) map.set(p.value ?? 'ALL_TIME', p);
    return map;
  }, []);

  const duplicateRangeLabels = useMemo(() => {
    const counts = new Map<string, number>();
    for (const p of TIMERANGE_PRESETS) counts.set(p.label, (counts.get(p.label) ?? 0) + 1);
    return new Set([...counts].filter(([, c]) => c > 1).map(([l]) => l));
  }, []);

  const rangeLabel = (token: string) => {
    const p = rangeByToken.get(token);
    if (!p) return token;
    return duplicateRangeLabels.has(p.label) ? `${p.label} · ${p.group}` : p.label;
  };

  const toggleRange = (token: string) => {
    setSelectedRanges((prev) => {
      const next = prev.includes(token) ? prev.filter((t) => t !== token) : [...prev, token];
      if (!next.includes(defaultRange)) setDefaultRange(next[0] ?? '');
      return next;
    });
  };

  const toggleAgg = (agg: string) => {
    setSelectedAggs((prev) => {
      const next = prev.includes(agg) ? prev.filter((a) => a !== agg) : [...prev, agg];
      if (!next.includes(defaultAgg)) setDefaultAgg(next[0] ?? '');
      return next;
    });
  };

  const toggleId = (set: Set<string>, setter: (s: Set<string>) => void, id: string) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setter(next);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader title="Generate Template" description="Create a dashboard template from an existing Cluvio dashboard." />
        <Card className="p-5 space-y-3">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
        </Card>
      </div>
    );
  }

  const buildAndSubmit = async () => {
    if (!dashboard) return;

    const fixedFilters = availableFilters
      .filter((f) => fixedFilterIds.has(f.id))
      .map((f) => ({
        fixedType: 'FILTER',
        key: f.variable_name,
        label: f.name,
        cluvioFilterID: f.id,
      }));

    const variableFilters = remainingFilters
      .filter((f) => variableFilterIds.has(f.id))
      .map((f) => {
        const vf: Record<string, unknown> = {
          fieldType: 'CLUVIO_FILTER',
          key: f.variable_name,
          label: f.name,
          cluvioFilterID: f.id,
          limitFilterOptions: limitedFilterIds.has(f.id),
        };
        const def = defaults[f.id];
        if (def) {
          if (Array.isArray(def)) {
            if (def.length > 0) vf.defaultFieldOptions = def.map((o) => ({ label: o.label, value: o.value }));
          } else if (def.value) {
            vf.defaultFieldOption = { label: def.label, value: def.value };
          }
        }
        if (placeholders[f.id]) {
          vf.placeholder = placeholders[f.id];
        }
        return vf;
      });

    if (hasAggregation && selectedAggs.length > 0) {
      variableFilters.push({
        fieldType: 'DROPDOWN',
        key: 'aggregation',
        label: 'Aggregation',
        defaultFieldOption: { label: defaultAgg, value: defaultAgg.toLowerCase() },
        fieldOptions: selectedAggs.map((a) => ({ label: a, value: a.toLowerCase() })),
      });
    }

    if (hasTimerange && selectedRanges.length > 0) {
      const defaultPreset = rangeByToken.get(defaultRange);
      variableFilters.push({
        fieldType: 'TIME_RANGE',
        key: 'timerange',
        label: 'Time Range',
        defaultFieldOption: defaultPreset ? { label: defaultPreset.label, value: defaultPreset.value } : undefined,
        fieldOptions: selectedRanges.map((token) => {
          const p = rangeByToken.get(token)!;
          return { label: p.label, value: p.value };
        }),
        allowCustomTimerange: allowCustom,
      });
    }

    const tenantType = typeOfTenant.toUpperCase().replace(/-/g, '_');

    const input = {
      name: templateName || dashboard.name,
      typeOfTenantID: typeOfTenant,
      description,
      defaultDashboardName: defaultDashboardName || dashboard.name,
      defaultDashboardSortOrder: Number(defaultSortOrder),
      defaultDashboardDescription,
      tenantType: tenantType === 'SELF_SERVE' ? 'NONE' : tenantType,
      cluvioDashboard: {
        id: dashboard.id,
        type: 'dashboards',
        dashboardAttributes: {},
      },
      cluvioSharingToken: {
        id: dashboard.sharing_token.id,
        dashboard_id: dashboard.id,
        token: dashboard.sharing_token.token,
      },
      fixedFilters,
      variableFilters,
      displayFilterOpen: enableCollapsible,
      defaultIsFilterOpen: filtersVisible,
    };

    try {
      await createMutation.mutateAsync(input);
      toast.success('Template created');
      setSelectedDashboard(null);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Creation failed');
    }
  };

  const filteredDashboards = (dashboards || [])
    .filter((d) => d.name.toLowerCase().includes(dashboardSearch.toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <PageHeader
        title="Generate Template"
        description="Turn a Cluvio dashboard into a reusable Mission Control template."
      />

      <StepCard step={1} title="Select Cluvio Dashboard" done={!!dashboard}>
        <div className="relative mb-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={dashboardSearch}
            onChange={(e) => setDashboardSearch(e.target.value)}
            placeholder="Search dashboards..."
            className="pl-9"
          />
        </div>
        {filteredDashboards.length === 0 ? (
          <EmptyState icon={BarChart3} title="No dashboards found" description="Try a different search term." />
        ) : (
          <div className="max-h-64 overflow-auto divide-y divide-border/60">
            {filteredDashboards.map((d) => (
              <button
                key={d.id}
                type="button"
                onClick={() => {
                  setSelectedDashboard(d.id);
                  setFixedFilterIds(new Set());
                  setVariableFilterIds(new Set());
                  setLimitedFilterIds(new Set());
                  setDefaults({});
                  setPlaceholders({});
                  setTemplateName(d.name);
                  setDefaultDashboardName(d.name);
                }}
                className={cn(
                  'w-full text-left px-2 py-2.5 transition-colors',
                  selectedDashboard === d.id ? 'bg-accent/60' : 'hover:bg-muted/50'
                )}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="font-medium text-sm truncate">{d.name}</div>
                  {selectedDashboard === d.id && <Check className="h-4 w-4 text-primary shrink-0" />}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {d.filters.length} filter{d.filters.length === 1 ? '' : 's'}
                  {d.has_timerange_editor ? ' · timerange' : ''}
                  {d.has_time_aggregation ? ' · aggregation' : ''}
                </div>
              </button>
            ))}
          </div>
        )}
      </StepCard>

      {dashboard && (
        <>
          <StepCard step={2} title="Tenant Type">
            <Select value={typeOfTenant} onChange={(e) => setTypeOfTenant(e.target.value)}>
              {TENANT_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Select>
          </StepCard>

          <StepCard step={3} title="Fixed Filters" description="Values are locked per tenant when the dashboard is created." done={fixedFilterIds.size > 0}>
            <div className="relative mb-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={fixedFilterSearch}
                onChange={(e) => setFixedFilterSearch(e.target.value)}
                placeholder="Search filters..."
                className="pl-9"
              />
            </div>
            <div className="space-y-1 max-h-64 overflow-auto pr-1">
              {availableFilters
                .filter((f) => f.name.toLowerCase().includes(fixedFilterSearch.toLowerCase()))
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((f) => (
                  <label
                    key={f.id}
                    className="flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm hover:bg-muted/60 transition-colors cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-primary"
                      checked={fixedFilterIds.has(f.id)}
                      onChange={() => toggleId(fixedFilterIds, setFixedFilterIds, f.id)}
                    />
                    {f.name}
                  </label>
                ))}
            </div>
          </StepCard>

          <StepCard step={4} title="Variable Filters" description="Shown to viewers; optionally preset a default value." done={variableFilterIds.size > 0}>
            <div className="relative mb-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={varFilterSearch}
                onChange={(e) => setVarFilterSearch(e.target.value)}
                placeholder="Search filters..."
                className="pl-9"
              />
            </div>
            <div className="max-h-72 overflow-auto divide-y divide-border/60">
              {remainingFilters
                .filter((f) => f.name.toLowerCase().includes(varFilterSearch.toLowerCase()))
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((f) => (
                  <div key={f.id} className="px-1 py-2.5">
                    <label className="flex items-center gap-2.5 text-sm font-medium cursor-pointer">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-primary"
                        checked={variableFilterIds.has(f.id)}
                        onChange={() => toggleId(variableFilterIds, setVariableFilterIds, f.id)}
                      />
                      {f.name}
                    </label>

                    {variableFilterIds.has(f.id) && (
                      <div className="mt-3 space-y-2.5 pl-7 animate-fade-in">
                        <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                          <input
                            type="checkbox"
                            className="h-3.5 w-3.5 accent-primary"
                            checked={limitedFilterIds.has(f.id)}
                            onChange={() => toggleId(limitedFilterIds, setLimitedFilterIds, f.id)}
                          />
                          Limit filter options
                        </label>
                        <FilterDefaultField
                          filter={f}
                          value={defaults[f.id]}
                          onChange={(opt) => setDefaults({ ...defaults, [f.id]: opt })}
                        />
                        <div className="space-y-1">
                          <span className="text-[11px] font-medium text-muted-foreground">Placeholder (optional)</span>
                          <Input
                            value={placeholders[f.id] || ''}
                            onChange={(e) => setPlaceholders({ ...placeholders, [f.id]: e.target.value })}
                            className="h-8 text-xs"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
            </div>
          </StepCard>

          {(dashboard.has_time_aggregation || dashboard.has_timerange_editor) && (
            <StepCard step={5} title="Time & Aggregation">
              <div className="space-y-4">
                {dashboard.has_time_aggregation && (
                  <div>
                    <label className="flex items-center gap-2.5 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-primary"
                        checked={hasAggregation}
                        onChange={(e) => setHasAggregation(e.target.checked)}
                      />
                      Include aggregation selector
                    </label>
                    {hasAggregation && (
                      <div className="mt-3 pl-7 space-y-3 animate-fade-in">
                        <OptionChips
                          options={AGGREGATIONS.map((a) => ({ label: a, value: a }))}
                          selected={selectedAggs}
                          onToggle={toggleAgg}
                        />
                        {selectedAggs.length > 0 && (
                          <Field label="Default aggregation">
                            <Select value={defaultAgg} onChange={(e) => setDefaultAgg(e.target.value)}>
                              {selectedAggs.map((a) => (
                                <option key={a} value={a}>
                                  {a}
                                </option>
                              ))}
                            </Select>
                          </Field>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {dashboard.has_timerange_editor && (
                  <div>
                    <label className="flex items-center gap-2.5 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-primary"
                        checked={hasTimerange}
                        onChange={(e) => setHasTimerange(e.target.checked)}
                      />
                      Include timerange selector
                    </label>
                    {hasTimerange && (
                      <div className="mt-3 pl-7 space-y-3 animate-fade-in">
                        {TIMERANGE_GROUP_ORDER.map((group) => {
                          const presets = TIMERANGE_PRESETS.filter((p) => p.group === group);
                          if (presets.length === 0) return null;
                          return (
                            <div key={group} className="space-y-1.5">
                              <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                                {group}
                              </span>
                              <OptionChips
                                options={presets.map((p) => ({
                                  label: duplicateRangeLabels.has(p.label) && p.group.startsWith('Last N (rolling')
                                    ? `${p.label} (rolling)`
                                    : p.label,
                                  value: p.value ?? 'ALL_TIME',
                                }))}
                                selected={selectedRanges}
                                onToggle={toggleRange}
                              />
                            </div>
                          );
                        })}
                        {selectedRanges.length > 0 && (
                          <Field label="Default time range">
                            <Select value={defaultRange} onChange={(e) => setDefaultRange(e.target.value)}>
                              {selectedRanges.map((token) => (
                                <option key={token} value={token}>
                                  {rangeLabel(token)}
                                </option>
                              ))}
                            </Select>
                          </Field>
                        )}
                        <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                          <input
                            type="checkbox"
                            className="h-3.5 w-3.5 accent-primary"
                            checked={allowCustom}
                            onChange={(e) => setAllowCustom(e.target.checked)}
                          />
                          Allow custom timeranges
                        </label>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </StepCard>
          )}

          <StepCard step={6} title="UI Options">
            <div className="space-y-2.5">
              <label className="flex items-center gap-2.5 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-primary"
                  checked={enableCollapsible}
                  onChange={(e) => setEnableCollapsible(e.target.checked)}
                />
                Collapsible filters section
              </label>
              {enableCollapsible && (
                <label className="flex items-center gap-2.5 text-sm pl-7 cursor-pointer animate-fade-in">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-primary"
                    checked={filtersVisible}
                    onChange={(e) => setFiltersVisible(e.target.checked)}
                  />
                  Filters visible by default
                </label>
              )}
            </div>
          </StepCard>

          <StepCard step={7} title="Template Details">
            <div className="space-y-3">
              <Field label="Template name">
                <Input value={templateName} onChange={(e) => setTemplateName(e.target.value)} />
              </Field>
              <Field label="Description">
                <Input value={description} onChange={(e) => setDescription(e.target.value)} />
              </Field>
              <Field label="Default dashboard name">
                <Input value={defaultDashboardName} onChange={(e) => setDefaultDashboardName(e.target.value)} />
              </Field>
              <Field label="Sort order">
                <Input type="number" value={defaultSortOrder} onChange={(e) => setDefaultSortOrder(e.target.value)} />
              </Field>
              <Field label="Default dashboard description">
                <Input
                  value={defaultDashboardDescription}
                  onChange={(e) => setDefaultDashboardDescription(e.target.value)}
                />
              </Field>
            </div>
          </StepCard>

          <Card className="p-5 space-y-4 animate-enter">
            <h2 className="text-sm font-semibold">Review & Create</h2>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
              <dt className="text-muted-foreground">Dashboard</dt>
              <dd className="font-medium truncate">{dashboard.name}</dd>
              <dt className="text-muted-foreground">Tenant type</dt>
              <dd className="font-medium">{typeOfTenant}</dd>
              <dt className="text-muted-foreground">Fixed filters</dt>
              <dd className="font-medium">{fixedFilterIds.size}</dd>
              <dt className="text-muted-foreground">Variable filters</dt>
              <dd className="font-medium">{variableFilterIds.size}</dd>
              {hasAggregation && selectedAggs.length > 0 && (
                <>
                  <dt className="text-muted-foreground">Aggregation</dt>
                  <dd className="font-medium">{selectedAggs.join(', ')} (default {defaultAgg || '—'})</dd>
                </>
              )}
              {hasTimerange && selectedRanges.length > 0 && (
                <>
                  <dt className="text-muted-foreground">Time ranges</dt>
                  <dd className="font-medium">
                    {selectedRanges.length} option{selectedRanges.length === 1 ? '' : 's'} · default{' '}
                    {defaultRange ? rangeLabel(defaultRange) : '—'}
                  </dd>
                </>
              )}
            </dl>
            <Button onClick={buildAndSubmit} disabled={createMutation.isPending} className="w-full">
              {createMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Wand2 className="h-4 w-4 mr-2" />
              )}
              Create Template
            </Button>
          </Card>
        </>
      )}
    </div>
  );
}
